import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ADS_FUNNEL_LIST_MAX_ITEMS, ADS_FUNNEL_SETTINGS_CATALOG_UNAVAILABLE, ADS_FUNNEL_TARGETED_MAX_COUNTRIES, ADS_FUNNEL_TARGETED_MAX_RULES, canonicalFunnelValue, isAdsCountryCode, sameFunnelValue, } from '@wit-support/admin-contracts';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { Badge, Button, InlineNotice, SectionCard, Skeleton } from '@wit-support/ui';
import { IssuesList } from './components/issues-list.js';
import { ZodField, useZodFormContext } from '@wit-support/ui-forms';
import { getCampaignFunnelSettings, getFunnelSettingsCatalog } from './api.js';
import { ADS_QUERY_DEFAULTS } from './query-policy.js';
import { describeAdsError } from './shared.js';
import { FunnelValueHint, TargetedFunnelField } from './funnel-targeting-field.js';
import { FUNNEL_MODE_ALL, FUNNEL_MODE_RULES, FUNNEL_OTHERWISE_UNSET, hasTargeting, newRuleId, isAgeRange, isEditableTargeted, isGender, isTargetedForm, isTargetedValue, targetedValueParts, targetingDims, } from './funnel-targeting.js';
/*
 * Настройки воронки от кампании (спека 2026-10-24 §11, план K6) на экранах
 * «Реклама»: секция редактора шаблона, шаг нового запуска, сводка в плане и
 * блок карточки запуска (только чтение — решение Р5).
 *
 * Человек видит названия настроек и вариантов из каталога, а не ключи и коды.
 *
 * Сохранение шаблона (auto-ads): нет поля `funnelSettings` — настройки
 * остаются как есть; пустые `values` и `launchEditable` — убрать. Поэтому
 * поле уходит, только если человек их действительно поменял; каталог
 * недоступен — менять нечем, поле не уходит никогда.
 *
 * Каталог снимается один раз при открытии формы (`useState`-снимок в
 * экранах): обновление каталога в фоне не перестраивает уже открытую форму.
 * Поля формы называются по ключу настройки (`funnelField`), а не по позиции.
 *
 * Значение с условиями (спека 2026-10-26 §3.1): у настройки с `targetable`
 * выбор «Для всех / С условиями» (`funnel-targeting-field.tsx`); изменение
 * ищется сравнением по канонической форме (`sameFunnelValue`): порядок ключей
 * и порядок стран / возрастов / пола в условии — не изменение, порядок
 * правил — изменение.
 *
 * Значение-список (вид `trialPlans`, спека 2026-10-26 «Варианты триала») —
 * набор тарифов: выбор нескольких (`multiselect`), сводка — подписи через
 * «, » (правила условий в сводке разделяет «; »). Это множество: порядок тарифов и повторы — не изменение. Тариф,
 * которого больше нет в каталоге, остаётся выбранным с подписью «Устаревший
 * тариф», пока его не уберут.
 */
export const FUNNEL_SETTINGS_CATALOG_KEY = ['ads', 'funnel-settings', 'catalog'];
/** Каталог настроек воронки (auto-ads сам кэширует его на 60 с). */
/** Текст загрузки каталога настроек воронки (L5) — один на все места ожидания. */
export const FUNNEL_CATALOG_LOADING_TEXT = 'Загружаем список настроек воронки…';
/** Ожидание каталога: текст + заглушка (секция формы, страница, карточка запуска). */
export function FunnelCatalogLoading({ height = 'h-16', testId = 'funnel-catalog-loading', }) {
    return (_jsxs("div", { className: "flex flex-col gap-2", "data-testid": testId, "aria-busy": "true", children: [_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: FUNNEL_CATALOG_LOADING_TEXT }), _jsx(Skeleton, { className: `${height} w-full` })] }));
}
export function useFunnelSettingsCatalog(enabled = true) {
    return useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: FUNNEL_SETTINGS_CATALOG_KEY,
        queryFn: getFunnelSettingsCatalog,
        staleTime: 60_000,
        // Сбой каталога не держит экран: сразу понятный текст, без повторов в фоне
        // (редактор шаблона и запуск ждут каталог, пока он грузится).
        retry: false,
        enabled,
    });
}
/** Фраза «каталог недоступен» для человека. */
export function funnelCatalogProblemText(err) {
    if (err instanceof ModuleRequestError &&
        err.upstreamCode === ADS_FUNNEL_SETTINGS_CATALOG_UNAVAILABLE) {
        return 'Настройки воронки сейчас недоступны: сервер приложения не отдал их список. Повторите позже.';
    }
    return `Не удалось загрузить список настроек воронки: ${describeAdsError(err)}`;
}
/** Каталог — последняя удачная копия (app-service не ответил): мягкое предупреждение. */
export function FunnelCatalogStaleNotice({ catalog, }) {
    if (!catalog?.stale)
        return null;
    return (_jsx(InlineNotice, { variant: "info", as: "div", "data-testid": "funnel-catalog-stale", children: "\u0421\u043F\u0438\u0441\u043E\u043A \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u043C\u043E\u0436\u0435\u0442 \u0431\u044B\u0442\u044C \u043D\u0435\u0430\u043A\u0442\u0443\u0430\u043B\u0435\u043D: \u0441\u0435\u0440\u0432\u0435\u0440 \u043F\u0440\u0438\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435 \u043E\u0442\u0432\u0435\u0442\u0438\u043B, \u043F\u043E\u043A\u0430\u0437\u0430\u043D\u0430 \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u044F\u044F \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u043D\u0430\u044F \u043A\u043E\u043F\u0438\u044F." }));
}
/** «Не задано» в выборе варианта (пустое значение Select не умеет). */
export const FUNNEL_NONE = '__none__';
/** Переключатель в форме — выбор из трёх: не задано / включено / выключено. */
export const FUNNEL_ON = 'on';
export const FUNNEL_OFF = 'off';
/** Текст для настройки, которой больше нет (или вариант которой убран из списка). */
export const FUNNEL_OUTDATED_TEXT = 'Эта настройка устарела — уберите её или выберите заново';
/** Список значений настройки временно недоступен (каталог без вариантов / последняя копия). */
export const FUNNEL_UNAVAILABLE_TEXT = 'Список значений сейчас недоступен — значение сохранится как есть';
/** «Не задано» — одна формулировка во всех полях. */
export const FUNNEL_NOT_SET_LABEL = 'Не задано — как настроено в воронке';
/** Один вариант из `options`. */
const VARIANT_KINDS = new Set(['enum', 'funnelConfig']);
/** Набор вариантов из `options` (значение-список). */
const LIST_KINDS = new Set(['trialPlans']);
const isListKind = (def) => LIST_KINDS.has(def.kind);
/** Вид, где значение выбирают из `options` (один или несколько). */
const isChoiceKind = (def) => VARIANT_KINDS.has(def.kind) || LIST_KINDS.has(def.kind);
/** Подпись i-го (с 0) устаревшего тарифа / варианта — различимы между собой, одинаковы в выборе и сводке. */
const outdatedLabel = (def, i) => `${isListKind(def) ? 'Устаревший тариф' : 'Устаревший вариант'} ${i + 1}`;
const isStringList = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
/** Настройка задаётся на уровне кампании (форма шаблона и запуска — только такие). */
export function isCampaignLevel(def) {
    return def.levels.includes('campaign');
}
/**
 * Настройку можно редактировать формой: уровень кампании, известный вид (и
 * есть варианты, где они нужны).
 */
export function isFormEditable(def) {
    if (!isCampaignLevel(def) || def.unavailable)
        return false;
    if (isChoiceKind(def))
        return (def.options ?? []).length > 0;
    return def.kind === 'boolean' || def.kind === 'number';
}
const inOptions = (def, v) => (def.options ?? []).some((o) => o.value === v);
/**
 * Значение-вариант, которого больше нет среди вариантов каталога; у списка —
 * в нём есть хотя бы один такой тариф.
 */
export function isOutdatedVariant(def, value) {
    if (isListKind(def))
        return isStringList(value) && value.some((v) => !inOptions(def, v));
    return VARIANT_KINDS.has(def.kind) && typeof value === 'string' && !inOptions(def, value);
}
/** Элементы списка, которых больше нет среди вариантов каталога (без повторов). */
function outdatedItems(def, values) {
    return [...new Set(values.filter((v) => !inOptions(def, v)))];
}
/** Обычные значения внутри значения: само значение или правила по порядку, затем «остальные». */
function plainsOf(value) {
    if (value === undefined)
        return [];
    if (!isTargetedValue(value))
        return [value];
    const all = [...value.rules.map((r) => r.value), value.otherwise];
    return all.filter((v) => v !== undefined);
}
/**
 * Устаревшие тарифы / варианты всего значения по порядку появления — по нему
 * нумеруются «Устаревший тариф N» и в выборе, и в сводке.
 */
function outdatedOrder(def, value) {
    const items = plainsOf(value).flatMap((v) => isListKind(def) ? (isStringList(v) ? v : []) : typeof v === 'string' ? [v] : []);
    return isChoiceKind(def) ? outdatedItems(def, items) : [];
}
/**
 * Обычное значение той формы, которой ждёт вид настройки: список — список
 * строк, вариант — строка, переключатель — boolean, число — число. Незнакомый
 * вид — любая не-списочная форма.
 */
function plainFitsKind(def, v) {
    if (isListKind(def))
        return isStringList(v);
    if (Array.isArray(v))
        return false;
    if (VARIANT_KINDS.has(def.kind))
        return typeof v === 'string';
    if (def.kind === 'boolean')
        return typeof v === 'boolean';
    if (def.kind === 'number')
        return typeof v === 'number';
    return true;
}
/** Название настройки; нет в каталоге — без ключа, словами. */
export function funnelLabelOf(def) {
    return def?.label ?? 'Настройка, которой нет в списке';
}
/** Первая буква — строчная («Выключена» → «выключена»), если это слово, а не аббревиатура. */
const lowerFirst = (t) => /^\p{Lu}\p{Ll}/u.test(t) ? `${t[0].toLowerCase()}${t.slice(1)}` : t;
/** Подписи списка: тарифы каталога в его порядке, устаревшие — «Устаревший тариф N» в конце. */
function listLabels(def, value, gone) {
    const set = new Set(value);
    const known = (def.options ?? []).filter((o) => set.has(o.value)).map((o) => o.label);
    const old = [...set]
        .filter((v) => !inOptions(def, v))
        .map((v) => outdatedLabel(def, gone.indexOf(v)));
    return [...known, ...old];
}
function plainParts(def, value, gone) {
    const text = (t) => ({ text: t, chips: null });
    if (def?.unavailable) {
        return text(isStringList(value) ? `выбрано: ${new Set(value).size}` : 'задано');
    }
    if (Array.isArray(value)) {
        if (new Set(value).size === 0)
            return text('ничего не выбрано');
        if (!def)
            return text('задано');
        if (!isListKind(def))
            return text('значение в старом формате');
        const chips = listLabels(def, value, gone);
        return { text: chips.join(' / '), chips };
    }
    // вид «список», а значение не той формы (скаляр из старых данных)
    if (def && isListKind(def))
        return text('значение в старом формате — выберите тарифы заново');
    if (def && !plainFitsKind(def, value))
        return text('значение в старом формате');
    if (typeof value === 'boolean')
        return text(value ? 'включено' : 'выключено');
    if (typeof value === 'number')
        return text(value.toLocaleString('ru-RU'));
    const option = def?.options?.find((o) => o.value === value);
    if (option)
        return text(option.label);
    if (def && isChoiceKind(def))
        return text(outdatedLabel(def, Math.max(0, gone.indexOf(value))));
    // нет в каталоге: значение — код, человеку его не показываем
    return text(def ? value : 'задано');
}
export function funnelValueParts(def, value) {
    const gone = def ? outdatedOrder(def, value) : [];
    if (isTargetedValue(value)) {
        // после стрелки — со строчной: «Германия → выключена»
        const inRule = (v) => {
            const p = plainParts(def, v, gone);
            return p.chips ? p : { text: lowerFirst(p.text), chips: null };
        };
        return { kind: 'rules', ...targetedValueParts(value, inRule) };
    }
    return { kind: 'plain', value: plainParts(def, value, gone) };
}
/**
 * Значение настройки словами одной строкой (подпись варианта, «включено»,
 * число; список — подписи через « / »); с правилами — «Германия →
 * выключена; остальные → включена». Список значений недоступен — с пометкой.
 */
export function funnelValueText(def, value) {
    const p = funnelValueParts(def, value);
    const text = p.kind === 'plain'
        ? p.value.text
        : [
            ...p.rules.map((r) => `${r.when} → ${r.value.text}`),
            `остальные → ${p.otherwise?.text ?? FUNNEL_OTHERWISE_UNSET}`,
        ].join('; ');
    return def?.unavailable ? `${text} (список значений сейчас недоступен)` : text;
}
/** Строки «название — значение» в порядке каталога, неизвестные — в конце. */
export function funnelSummaryRows(values, catalog) {
    const defs = new Map((catalog?.settings ?? []).map((d) => [d.key, d]));
    const order = new Map((catalog?.settings ?? []).map((d, i) => [d.key, i]));
    return Object.keys(values)
        .sort((a, b) => (order.get(a) ?? 1e9) - (order.get(b) ?? 1e9))
        .map((key) => {
        const def = defs.get(key);
        return { key, label: funnelLabelOf(def), value: funnelValueText(def, values[key]) };
    });
}
// ---------------------------------------------------------------------------
// Поля формы
// ---------------------------------------------------------------------------
/**
 * Имя поля формы по ключу настройки. react-hook-form читает точку (и `[`)
 * как путь, поэтому всё, кроме латиницы и цифр, кодируется `_<hex>_` —
 * взаимно однозначно (сам `_` тоже кодируется), разные ключи не совпадут.
 */
export function funnelField(key) {
    return `k_${key.replace(/[^A-Za-z0-9]/g, (c) => `_${c.charCodeAt(0).toString(16)}_`)}`;
}
/**
 * Выбор нескольких тарифов (`multiselect`). Выбрать можно только тарифы
 * каталога и устаревшие тарифы, которые уже были в значении (`kept`), не
 * больше `ADS_FUNNEL_LIST_MAX_ITEMS`. Пусто — «не задано» (у правила это
 * ошибка «Выберите хотя бы один тариф», её даёт схема правил).
 */
function listValueSchema(def, gone, label) {
    const options = [
        ...(def.options ?? []),
        ...gone.map((v, i) => ({ value: v, label: outdatedLabel(def, i) })),
    ];
    const allowed = new Set(options.map((o) => o.value));
    // `optional`: новое правило ещё без значения — своя ошибка «Выберите хотя бы один тариф»
    return z
        .array(z.string())
        .superRefine((list, ctx) => {
        if (list.some((v) => !allowed.has(v))) {
            ctx.addIssue({ code: 'custom', message: 'Тариф не из списка — уберите его' });
        }
        if (new Set(list).size > ADS_FUNNEL_LIST_MAX_ITEMS) {
            ctx.addIssue({
                code: 'custom',
                message: `Тарифов — не больше ${ADS_FUNNEL_LIST_MAX_ITEMS}`,
            });
        }
    })
        .optional()
        .meta({
        label,
        widget: 'multiselect',
        options,
        max: ADS_FUNNEL_LIST_MAX_ITEMS,
        placeholder: FUNNEL_NOT_SET_LABEL,
    });
}
/**
 * Схема поля значения настройки (`ZodForm` выбирает виджет: выбор / несколько
 * / число). `gone` — устаревшие тарифы / варианты всего значения (по ним
 * нумерация «Устаревший … N»; остаются выбираемыми, пока их не уберут).
 */
export function funnelValueSchema(def, current, gone = outdatedOrder(def, current)) {
    if (isListKind(def))
        return listValueSchema(def, gone, def.label);
    if (VARIANT_KINDS.has(def.kind)) {
        const options = [
            { value: FUNNEL_NONE, label: FUNNEL_NOT_SET_LABEL },
            ...(def.options ?? []),
            ...gone.map((v, i) => ({ value: v, label: outdatedLabel(def, i) })),
        ];
        const values = options.map((o) => o.value);
        return z.enum(values).meta({ label: def.label, options });
    }
    if (def.kind === 'boolean') {
        const options = [
            { value: FUNNEL_NONE, label: FUNNEL_NOT_SET_LABEL },
            { value: FUNNEL_ON, label: 'Включено' },
            { value: FUNNEL_OFF, label: 'Выключено' },
        ];
        return z.enum([FUNNEL_NONE, FUNNEL_ON, FUNNEL_OFF]).meta({ label: def.label, options });
    }
    return z
        .number({ message: 'Нужно число' })
        .optional()
        .meta({ label: def.label, placeholder: FUNNEL_NOT_SET_LABEL });
}
/** Значение настройки → значение поля формы. */
export function toFunnelFormValue(def, value) {
    if (isListKind(def))
        return isStringList(value) ? [...new Set(value)] : [];
    if (VARIANT_KINDS.has(def.kind))
        return typeof value === 'string' ? value : FUNNEL_NONE;
    if (def.kind === 'boolean') {
        return value === true ? FUNNEL_ON : value === false ? FUNNEL_OFF : FUNNEL_NONE;
    }
    return typeof value === 'number' ? value : undefined;
}
/**
 * Поле формы → значение настройки; `undefined` — не задано. Список уходит в
 * канонической форме (без повторов, отсортирован) — порядок выбора не значим.
 */
export function fromFunnelFormValue(def, form) {
    if (isListKind(def)) {
        return isStringList(form) && form.length > 0 ? canonicalFunnelValue(form) : undefined;
    }
    if (VARIANT_KINDS.has(def.kind)) {
        return typeof form === 'string' && form !== FUNNEL_NONE ? form : undefined;
    }
    if (def.kind === 'boolean') {
        return form === FUNNEL_ON ? true : form === FUNNEL_OFF ? false : undefined;
    }
    return typeof form === 'number' && Number.isFinite(form) ? form : undefined;
}
// --- значение с условиями ----------------------------------------------------
/**
 * У поля настройки — выбор «Для всех / С условиями»: каталог разрешает
 * условия, или значение уже с правилами (условия убрали из каталога — форма
 * честно просит выбрать «Для всех»).
 */
export function usesTargetedForm(def, current, dims) {
    return (def.targetable && hasTargeting(dims)) || isTargetedValue(current);
}
/**
 * Обычное значение той формы, которую редактирует поле (по виду настройки:
 * список, вариант-строка, переключатель, число). Иначе форма молча потеряла
 * бы значение — такая настройка только показывается и её можно убрать.
 */
function fitsPlain(def, v) {
    return v === undefined || plainFitsKind(def, v);
}
/**
 * Значение умещается в форму без потерь: форма значения — по виду настройки
 * (список / не список), правила — только из известных условий.
 */
export function fitsFunnelForm(def, value, dims) {
    if (!isTargetedValue(value))
        return fitsPlain(def, value);
    return (isEditableTargeted(value, dims) &&
        value.rules.every((r) => fitsPlain(def, r.value)) &&
        fitsPlain(def, value.otherwise));
}
/** Значение правила: тот же виджет, что «для всех», но «Не задано» нельзя. */
function ruleValueSchema(def, current) {
    const gone = outdatedOrder(def, current);
    if (isListKind(def))
        return listValueSchema(def, gone, 'Значение');
    if (VARIANT_KINDS.has(def.kind)) {
        const options = [
            ...(def.options ?? []),
            ...gone.map((v, i) => ({ value: v, label: outdatedLabel(def, i) })),
        ];
        const values = options.map((o) => o.value);
        return z.enum(values).optional().meta({ label: 'Значение', options });
    }
    if (def.kind === 'boolean') {
        const options = [
            { value: FUNNEL_ON, label: 'Включено' },
            { value: FUNNEL_OFF, label: 'Выключено' },
        ];
        return z.enum([FUNNEL_ON, FUNNEL_OFF]).optional().meta({ label: 'Значение', options });
    }
    return z.number({ message: 'Нужно число' }).optional().meta({ label: 'Значение' });
}
const ruleIsEmpty = (r) => r.countries.length === 0 && r.ageRanges.length === 0 && r.genders.length === 0;
/**
 * Схема поля с условиями: режим, «для всех», правила, «остальные». Правила
 * проверяются только в режиме «С условиями» (в режиме «Для всех» они не
 * уходят и не мешают сохранить).
 */
function targetedFieldSchema(def, current, dims) {
    const rule = z.object({
        id: z.string().optional(),
        countries: z.array(z.string()),
        ageRanges: z.array(z.string()),
        genders: z.array(z.string()),
        value: ruleValueSchema(def, current),
    });
    const targeted = isTargetedValue(current) ? current : null;
    return z
        .object({
        mode: z.enum([FUNNEL_MODE_ALL, FUNNEL_MODE_RULES]),
        all: funnelValueSchema(def, targeted ? undefined : current),
        rules: z.array(rule),
        // нумерация устаревших — по всему значению, как в правилах
        otherwise: funnelValueSchema(def, targeted?.otherwise, outdatedOrder(def, current)),
    })
        .superRefine((f, ctx) => {
        if (f.mode !== FUNNEL_MODE_RULES)
            return;
        if (!(def.targetable && hasTargeting(dims))) {
            // каталог снял условия, а правила не трогали — только предупреждение
            // (`funnel-targeting-unsupported` в поле), сохранение шаблона не блокируем
            if (targeted && sameFunnelValue(fromTargetedForm(def, f), targeted))
                return;
            ctx.addIssue({
                code: 'custom',
                path: ['mode'],
                message: 'Условия для этой настройки больше не поддерживаются — выберите «Для всех»',
            });
            return;
        }
        if (f.rules.length === 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['rules'],
                message: 'Добавьте хотя бы одно правило или выберите «Для всех»',
            });
        }
        if (f.rules.length > ADS_FUNNEL_TARGETED_MAX_RULES) {
            ctx.addIssue({
                code: 'custom',
                path: ['rules'],
                message: `Правил — не больше ${ADS_FUNNEL_TARGETED_MAX_RULES}`,
            });
        }
        f.rules.forEach((r, i) => {
            if (ruleIsEmpty(r)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['rules', i, 'when'],
                    message: 'Задайте условие: страну, возраст или пол',
                });
            }
            // входная схема — структурная (смысл проверяет auto-ads): что редактирует
            // форма, она проверяет строго сама — страны только из справочника админки
            if (r.countries.some((c) => !isAdsCountryCode(c))) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['rules', i, 'countries'],
                    message: 'Страна не из списка стран — уберите её',
                });
            }
            if (r.countries.length > ADS_FUNNEL_TARGETED_MAX_COUNTRIES) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['rules', i, 'countries'],
                    message: `Не больше ${ADS_FUNNEL_TARGETED_MAX_COUNTRIES} стран`,
                });
            }
            if (fromFunnelFormValue(def, r.value) === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['rules', i, 'value'],
                    message: isListKind(def) ? 'Выберите хотя бы один тариф' : 'Выберите значение',
                });
            }
        });
    });
}
/** Значение правила → поле формы (без «Не задано»: пусто — `undefined`). */
function toRuleFormValue(def, value) {
    const v = toFunnelFormValue(def, value);
    return v === FUNNEL_NONE ? undefined : v;
}
function targetedFieldDefault(def, current) {
    if (!isTargetedValue(current)) {
        return {
            mode: FUNNEL_MODE_ALL,
            all: toFunnelFormValue(def, current),
            rules: [],
            otherwise: toFunnelFormValue(def, undefined),
        };
    }
    return {
        mode: FUNNEL_MODE_RULES,
        all: toFunnelFormValue(def, undefined),
        rules: current.rules.map((r) => ({
            id: newRuleId(),
            countries: [...(r.when['countries'] ?? [])],
            ageRanges: [...(r.when['ageRanges'] ?? [])],
            genders: [...(r.when['genders'] ?? [])],
            value: toRuleFormValue(def, r.value),
        })),
        otherwise: toFunnelFormValue(def, current.otherwise),
    };
}
function fromTargetedForm(def, f) {
    if (f.mode !== FUNNEL_MODE_RULES)
        return fromFunnelFormValue(def, f.all);
    const rules = [];
    for (const r of f.rules) {
        const value = fromFunnelFormValue(def, r.value);
        // схема не пропускает правило без значения; сюда оно не доходит
        if (value === undefined)
            continue;
        const when = {};
        if (r.countries.length > 0)
            when.countries = [...r.countries];
        const ages = r.ageRanges.filter(isAgeRange);
        if (ages.length > 0)
            when.ageRanges = ages;
        const genders = r.genders.filter(isGender);
        if (genders.length > 0)
            when.genders = genders;
        rules.push({ when, value });
    }
    if (rules.length === 0)
        return undefined;
    const otherwise = fromFunnelFormValue(def, f.otherwise);
    const out = { rules };
    if (otherwise !== undefined)
        out.otherwise = otherwise;
    return out;
}
/** Схема поля настройки: обычное значение или с условиями. */
export function funnelFieldSchema(def, current, dims) {
    return usesTargetedForm(def, current, dims)
        ? targetedFieldSchema(def, current, dims)
        : funnelValueSchema(def, current);
}
/** Начальное значение поля формы настройки. */
export function funnelFieldDefault(def, current, dims) {
    return usesTargetedForm(def, current, dims)
        ? targetedFieldDefault(def, current)
        : toFunnelFormValue(def, current);
}
/** Поле формы настройки → значение; `undefined` — не задано. */
export function funnelFieldValue(def, form) {
    return isTargetedForm(form) ? fromTargetedForm(def, form) : fromFunnelFormValue(def, form);
}
/**
 * Значение, которое форма не редактирует (устаревшая настройка), уходит
 * обратно как было получено; проверяет его auto-ads.
 */
const asStoredValue = (v) => v;
/** Поле настройки в форме: обычный виджет или «Для всех / С условиями». */
function FunnelSettingField({ def, targeted, dims, }) {
    const name = `funnel.v.${funnelField(def.key)}`;
    return targeted ? (_jsx(TargetedFunnelField, { def: def, name: name, dims: dims })) : (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ZodField, { name: name }), _jsx(FunnelValueHint, { settingKey: def.key, path: name })] }));
}
/** Обычное значение: текст или чипы тарифов. */
function PlainValueView({ value }) {
    if (!value.chips)
        return _jsx(_Fragment, { children: value.text });
    return (_jsx("span", { className: "inline-flex flex-wrap gap-1 align-middle", children: value.chips.map((c, i) => (_jsx(Badge, { variant: "outline", children: c }, `${c}-${i}`))) }));
}
/**
 * Значение настройки для чтения: обычное — текстом (список тарифов — чипами),
 * с правилами — нумерованным списком «условие → значение» и строкой
 * «остальные → …».
 */
export function FunnelValueView({ def, value, }) {
    const p = funnelValueParts(def, value);
    const note = def?.unavailable ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: [' ', "(\u0441\u043F\u0438\u0441\u043E\u043A \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u0439 \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D)"] })) : null;
    if (p.kind === 'plain') {
        return (_jsxs("span", { "data-testid": "funnel-value", children: [_jsx(PlainValueView, { value: p.value }), note] }));
    }
    return (_jsxs("div", { className: "flex flex-col gap-0.5", "data-testid": "funnel-value", children: [_jsx("ol", { className: "flex list-decimal flex-col gap-0.5 pl-5", children: p.rules.map((r, i) => (_jsxs("li", { children: [r.when, " \u2192 ", _jsx(PlainValueView, { value: r.value })] }, i))) }), _jsxs("span", { children: ["\u043E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435 \u2192 ", p.otherwise ? _jsx(PlainValueView, { value: p.otherwise }) : FUNNEL_OTHERWISE_UNSET] }), note] }));
}
/** Список «название — значение» (только чтение). */
export function FunnelValuesList({ values, catalog, testId, }) {
    if (!catalog) {
        const n = Object.keys(values).length;
        return (_jsx("span", { className: "text-sm", "data-testid": testId, children: `Задано настроек: ${n}. Названия появятся, когда список настроек снова будет доступен.` }));
    }
    const rows = funnelSummaryRows(values, catalog);
    const defs = new Map(catalog.settings.map((d) => [d.key, d]));
    return (_jsx("dl", { className: "grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm", "data-testid": testId, children: rows.map((r) => (_jsxs("div", { className: "contents", children: [_jsx("dt", { className: "text-[var(--color-text-muted)]", children: r.label }), _jsx("dd", { children: _jsx(FunnelValueView, { def: defs.get(r.key), value: values[r.key] }) })] }, r.key))) }));
}
export function templateFunnelModel(catalog, catalogError, original) {
    const dims = targetingDims(catalog);
    const values = original?.values ?? {};
    const defs = (catalog?.settings ?? []).filter((d) => isFormEditable(d) && fitsFunnelForm(d, values[d.key], dims));
    const shown = new Set(defs.map((d) => d.key));
    const byKey = new Map((catalog?.settings ?? []).map((d) => [d.key, d]));
    const editable = new Set(original?.launchEditable ?? []);
    const keys = [...new Set([...Object.keys(values), ...editable])].filter((k) => !shown.has(k));
    const outdated = catalog
        ? keys.map((key) => {
            const def = byKey.get(key);
            return {
                key,
                def,
                value: values[key],
                launchEditable: editable.has(key),
                // выбор без вариантов в каталоге бэкенд отклонит при любом сохранении — как устаревший;
                // список временно недоступен (или каталог — последняя копия без этой настройки) — нет
                reason: def?.unavailable === true || (!def && catalog.stale)
                    ? 'unavailable'
                    : def && (isFormEditable(def) || (isCampaignLevel(def) && !isChoiceKind(def)))
                        ? 'unsupported'
                        : 'outdated',
            };
        })
        : [];
    const targeted = new Set(defs.filter((d) => usesTargetedForm(d, values[d.key], dims)).map((d) => d.key));
    return { catalog, catalogError, defs, outdated, original, dims, targeted };
}
/**
 * Часть схемы формы шаблона: `funnel.v.<поле>` — значение, `funnel.e.<поле>` —
 * «можно менять при запуске», `funnel.drop` — ключи устаревших настроек,
 * которые человек убрал.
 */
export function templateFunnelSchema(model) {
    const v = {};
    const e = {};
    for (const d of model.defs) {
        v[funnelField(d.key)] = funnelFieldSchema(d, model.original?.values[d.key], model.dims);
        e[funnelField(d.key)] = z.boolean().meta({ label: 'можно менять при запуске' });
    }
    return z.object({ v: z.object(v), e: z.object(e), drop: z.array(z.string()) });
}
export function templateFunnelDefaults(model) {
    const v = {};
    const e = {};
    for (const d of model.defs) {
        v[funnelField(d.key)] = funnelFieldDefault(d, model.original?.values[d.key], model.dims);
        e[funnelField(d.key)] = model.original?.launchEditable.includes(d.key) ?? false;
    }
    return { v, e, drop: [] };
}
function sameFunnel(a, b) {
    const ak = Object.keys(a.values);
    if (ak.length !== Object.keys(b.values).length)
        return false;
    if (ak.some((k) => !sameFunnelValue(a.values[k], b.values[k])))
        return false;
    const ae = new Set(a.launchEditable);
    const be = new Set(b.launchEditable);
    return ae.size === be.size && [...ae].every((k) => be.has(k));
}
/**
 * Значения формы → `funnelSettings` шаблона. `undefined` — поле не
 * отправляется и auto-ads оставляет настройки как есть: каталог недоступен
 * (менять нечем) или человек ничего не поменял. Всё убрано — пустые
 * `values` и `launchEditable` (auto-ads удаляет настройки).
 */
export function templateFunnelInput(model, form) {
    if (!model.catalog)
        return undefined;
    const dropped = new Set(Array.isArray(form?.drop) ? form.drop : []);
    const values = {};
    const launchEditable = [];
    for (const o of model.outdated) {
        if (dropped.has(o.key))
            continue;
        if (o.value !== undefined)
            values[o.key] = asStoredValue(o.value);
        if (o.launchEditable)
            launchEditable.push(o.key);
    }
    for (const d of model.defs) {
        const field = funnelField(d.key);
        const val = funnelFieldValue(d, form?.v?.[field]);
        if (val !== undefined)
            values[d.key] = val;
        if (form?.e?.[field] === true)
            launchEditable.push(d.key);
    }
    const original = model.original ?? { values: {}, launchEditable: [] };
    if (sameFunnel(original, { values, launchEditable }))
        return undefined;
    return { values, launchEditable };
}
/**
 * Устаревший вариант под полем: честный текст, без кодов. Список — пока в
 * выборе остаётся хотя бы один устаревший тариф.
 */
function OutdatedVariantHint({ def, original }) {
    const { form } = useZodFormContext();
    const current = form.watch(`funnel.v.${funnelField(def.key)}`);
    // поле с условиями: устаревший вариант виден в своём выборе
    if (isTargetedForm(current))
        return null;
    if (isListKind(def)) {
        if (!isStringList(current) || outdatedItems(def, current).length === 0)
            return null;
        return (_jsx(InlineNotice, { variant: "warning", as: "div", "data-testid": `funnel-outdated-variant-${def.key}`, children: "\u0412\u044B\u0431\u0440\u0430\u043D \u0443\u0441\u0442\u0430\u0440\u0435\u0432\u0448\u0438\u0439 \u0442\u0430\u0440\u0438\u0444 \u2014 \u0435\u0433\u043E \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435\u0442 \u0432 \u0441\u043F\u0438\u0441\u043A\u0435, \u0443\u0431\u0435\u0440\u0438\u0442\u0435 \u0435\u0433\u043E." }));
    }
    else if (current !== original) {
        return null;
    }
    return (_jsxs(InlineNotice, { variant: "warning", as: "div", "data-testid": `funnel-outdated-variant-${def.key}`, children: [FUNNEL_OUTDATED_TEXT, "."] }));
}
/** Устаревшие настройки шаблона: честный текст и кнопка «Убрать». */
function OutdatedFunnelList({ items }) {
    const { form } = useZodFormContext();
    const dropped = form.watch('funnel.drop') ?? [];
    const setDropped = (next) => form.setValue('funnel.drop', next, { shouldDirty: true, shouldTouch: true });
    return (_jsx("div", { className: "flex flex-col gap-2", "data-testid": "funnel-outdated", children: items.map((o) => {
            const gone = dropped.includes(o.key);
            return (_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--color-border-subtle)] p-2", "data-testid": "funnel-outdated-item", "data-reason": o.reason, children: [_jsxs("div", { className: "flex flex-col gap-0.5 text-sm", children: [_jsxs("span", { children: [funnelLabelOf(o.def), o.def && o.value !== undefined ? `: ${funnelValueText(o.def, o.value)}` : ''] }), _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: gone
                                    ? 'Будет убрана при сохранении шаблона.'
                                    : o.reason === 'outdated'
                                        ? `${FUNNEL_OUTDATED_TEXT}.`
                                        : o.reason === 'unavailable'
                                            ? `${FUNNEL_UNAVAILABLE_TEXT}.`
                                            : 'Эту настройку форма пока не умеет менять — она сохранится как есть, или уберите её.' })] }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => setDropped(gone ? dropped.filter((k) => k !== o.key) : [...dropped, o.key]), children: gone ? 'Вернуть' : 'Убрать' })] }, o.key));
        }) }));
}
/**
 * Форма открылась без каталога, а он пришёл: форму не трогали — она
 * пересобирается сама; трогали — экран предлагает показать настройки (значения
 * формы сохраняются).
 */
function useCatalogArrival(model, control) {
    const { form } = useZodFormContext();
    const dirty = form.formState.isDirty;
    const arrived = model.catalog == null && control?.latest != null;
    useEffect(() => {
        if (arrived && !dirty)
            control?.rebuild(null);
    }, [arrived, dirty, control]);
    return arrived;
}
/** Нет каталога в форме: грузится / пришёл позже / не загрузился (с «Повторить»). */
function CatalogMissingNotice({ model, control, unavailableText, variant, }) {
    const { form } = useZodFormContext();
    const arrived = useCatalogArrival(model, control);
    if (arrived) {
        return (_jsx(InlineNotice, { variant: "info", as: "div", "data-testid": "funnel-catalog-arrived", children: _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("span", { children: "\u0421\u043F\u0438\u0441\u043E\u043A \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u043B\u0441\u044F." }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => control?.rebuild(form.getValues()), children: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438" })] }) }));
    }
    if (control?.loading && model.catalogError == null) {
        return _jsx(FunnelCatalogLoading, {});
    }
    return (_jsx(InlineNotice, { variant: variant, as: "div", "data-testid": "funnel-catalog-unavailable", children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx("span", { children: unavailableText }), control ? (_jsx("div", { children: _jsx(Button, { type: "button", size: "sm", variant: "secondary", disabled: control.retrying, onClick: control.retry, children: control.retrying ? 'Повторяем…' : 'Повторить' }) })) : null] }) }));
}
/** Подпись поля замечания настроек воронки: «Настройки воронки», «Настройки воронки · Варианты триала». */
export function funnelIssueLabel(fieldKey, catalog) {
    if (fieldKey === 'funnelSettings')
        return 'Настройки воронки';
    if (!fieldKey.startsWith('funnelSettings.'))
        return null;
    const key = fieldKey.slice('funnelSettings.'.length);
    const def = catalog?.settings.find((d) => d.key === key);
    return `Настройки воронки · ${def ? def.label : 'настройка, которой нет в списке'}`;
}
/** Ключ настройки замечания: `details.settingKey` или хвост `funnelSettings.<key>`. */
function issueSettingKey(i) {
    if (i.details?.settingKey)
        return i.details.settingKey;
    if (i.fieldKey?.startsWith('funnelSettings.'))
        return i.fieldKey.slice('funnelSettings.'.length);
    return null;
}
/** Замечание относится к настройкам воронки. */
export const isFunnelIssue = (i) => i.fieldKey === 'funnelSettings' || issueSettingKey(i) !== null;
/**
 * Поле формы, под которым показать замечание настройки воронки: правило N —
 * его значение, «остальные» — их поле, иначе — поле настройки (у поля с
 * условиями — под выбором «Для всех / С условиями»). Настройки нет в форме —
 * null (замечание показывается списком в секции).
 */
/** Измерения условия, у которых в карточке правила своё поле. */
const RULE_DIMENSION_FIELDS = new Set(['countries', 'ageRanges', 'genders']);
export function funnelIssueFieldPath(model, issue) {
    const key = issueSettingKey(issue);
    if (key === null)
        return null;
    const defs = model.defs ?? model.editable ?? [];
    if (!defs.some((d) => d.key === key))
        return null;
    const base = `funnel.v.${funnelField(key)}`;
    if (!model.targeted.has(key))
        return base;
    const place = issue.details?.place;
    if (typeof place === 'object' && place !== null && place.rule >= 1) {
        const rule = `${base}.rules.${place.rule - 1}`;
        // L4: замечание условия — на поле его измерения (страны / возраст / пол), без
        // измерения — на условие правила целиком; остальное — на значение правила
        const dim = issue.details?.dimension;
        if (dim !== undefined && RULE_DIMENSION_FIELDS.has(dim))
            return `${rule}.${dim}`;
        if (issue.code.startsWith('funnel-setting-condition-'))
            return `${rule}.when`;
        return `${rule}.value`;
    }
    if (place === 'otherwise')
        return `${base}.otherwise`;
    return `${base}.mode`;
}
/** Секция «Настройки воронки» редактора шаблона (внутри его `ZodForm`). */
export function TemplateFunnelSection({ model, control, issues = [], }) {
    const hint = (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0412 Meta \u043D\u0435 \u0443\u0445\u043E\u0434\u044F\u0442 \u2014 \u0438\u0445 \u043F\u043E\u043B\u0443\u0447\u0430\u0435\u0442 \u0432\u043E\u0440\u043E\u043D\u043A\u0430 \u0443 \u043B\u044E\u0434\u0435\u0439 \u0438\u0437 \u044D\u0442\u043E\u0439 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438" }));
    return (_jsx(SectionCard, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438", hint: hint, children: _jsxs("div", { className: "flex flex-col gap-4", "data-testid": "template-funnel-settings", children: [!model.catalog ? (_jsx(CatalogMissingNotice, { model: model, control: control, variant: "warning", unavailableText: `${funnelCatalogProblemText(model.catalogError)} ${model.original
                        ? 'Шаблон можно сохранить — настройки воронки останутся как были.'
                        : 'Шаблон можно сохранить без настроек воронки.'}` })) : (_jsx(FunnelCatalogStaleNotice, { catalog: model.catalog })), issues.length > 0 ? (_jsx("div", { "data-testid": "funnel-issues", children: _jsx(IssuesList, { issues: issues, labelOf: (k) => funnelIssueLabel(k, model.catalog) ?? k }) })) : null, model.catalog && model.defs.length === 0 && model.outdated.length === 0 ? (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", "data-testid": "funnel-empty", children: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u043F\u043E\u043A\u0430 \u043D\u0435 \u0437\u0430\u0434\u0430\u043D\u044B \u2014 \u0432\u044B\u0431\u0438\u0440\u0430\u0442\u044C \u043D\u0435\u0447\u0435\u0433\u043E." })) : null, model.defs.map((d) => (_jsxs("div", { className: "flex flex-col gap-1 border-b border-[var(--color-border-subtle)] pb-3 last:border-b-0 last:pb-0", "data-testid": `funnel-setting-${funnelField(d.key)}`, children: [_jsx(FunnelSettingField, { def: d, targeted: model.targeted.has(d.key), dims: model.dims }), isOutdatedVariant(d, model.original?.values[d.key]) ? (_jsx(OutdatedVariantHint, { def: d, original: model.original?.values[d.key] })) : null, _jsx(ZodField, { name: `funnel.e.${funnelField(d.key)}` })] }, d.key))), model.outdated.length > 0 ? _jsx(OutdatedFunnelList, { items: model.outdated }) : null, !model.catalog && model.original && Object.keys(model.original.values).length > 0 ? (_jsx(FunnelValuesList, { values: model.original.values, catalog: null })) : null] }) }));
}
/** У шаблона есть настройки воронки (значения или разрешённые к запуску ключи). */
export function hasTemplateFunnel(fs) {
    return fs != null && (Object.keys(fs.values).length > 0 || fs.launchEditable.length > 0);
}
export function launchFunnelModel(fs, catalog, catalogError) {
    const allowed = new Set(fs?.launchEditable ?? []);
    const dims = targetingDims(catalog);
    const values = fs?.values ?? {};
    // каталога нет — менять нечем; правила, которые форма не умеет, — только показать
    const editable = (catalog?.settings ?? []).filter((d) => allowed.has(d.key) && isFormEditable(d) && fitsFunnelForm(d, values[d.key], dims));
    const targeted = new Set(editable.filter((d) => usesTargetedForm(d, values[d.key], dims)).map((d) => d.key));
    const shown = new Set(editable.map((d) => d.key));
    const byKey = new Map((catalog?.settings ?? []).map((d) => [d.key, d]));
    const blocked = catalog
        ? [...allowed]
            .filter((k) => !shown.has(k))
            .map((key) => {
            const def = byKey.get(key);
            return {
                key,
                def,
                reason: def?.unavailable === true || (!def && catalog.stale) ? 'unavailable' : 'unsupported',
            };
        })
        : [];
    return { catalog, catalogError, values, editable, blocked, dims, targeted };
}
export function launchFunnelSchema(model) {
    const v = {};
    for (const d of model.editable) {
        v[funnelField(d.key)] = funnelFieldSchema(d, model.values[d.key], model.dims);
    }
    return z.object({ v: z.object(v) });
}
export function launchFunnelDefaults(model) {
    const v = {};
    for (const d of model.editable) {
        v[funnelField(d.key)] = funnelFieldDefault(d, model.values[d.key], model.dims);
    }
    return { v };
}
/**
 * Значения формы → `funnelSettings` пачки: только разрешённые ключи и только
 * отличия от шаблона (`null` — значение шаблона убрано). Отличий нет —
 * `undefined` (поле не уходит, план тот же, что без настроек).
 */
export function launchFunnelInput(model, form) {
    const out = {};
    for (const d of model.editable) {
        const tpl = model.values[d.key];
        const val = funnelFieldValue(d, form?.v?.[funnelField(d.key)]);
        if (val === undefined) {
            if (tpl !== undefined)
                out[d.key] = null;
        }
        else if (!sameFunnelValue(val, tpl)) {
            out[d.key] = val;
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Итоговые значения запуска: шаблон + изменения (`null` — убрано). */
export function effectiveFunnelValues(values, overrides) {
    const out = { ...values };
    for (const [k, v] of Object.entries(overrides ?? {})) {
        if (v === null)
            delete out[k];
        else
            out[k] = v;
    }
    return out;
}
/** Шаг «Настройки воронки» нового запуска (внутри его `ZodForm`). */
export function LaunchFunnelStep({ model, title, control, }) {
    const editableKeys = new Set(model.editable.map((d) => d.key));
    const blockedKeys = new Set(model.blocked.map((b) => b.key));
    // «Задано шаблоном» — только то, что шаблон менять при запуске не разрешает
    const fixed = Object.fromEntries(Object.entries(model.values).filter(([k]) => !editableKeys.has(k) && !blockedKeys.has(k)));
    return (_jsx(SectionCard, { title: title, hint: _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0438\u0437 \u0448\u0430\u0431\u043B\u043E\u043D\u0430; \u043C\u0435\u043D\u044F\u0442\u044C \u043C\u043E\u0436\u043D\u043E \u0442\u043E\u043B\u044C\u043A\u043E \u0440\u0430\u0437\u0440\u0435\u0448\u0451\u043D\u043D\u044B\u0435 \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435" }), children: _jsxs("div", { className: "flex flex-col gap-4", "data-testid": "launch-funnel-settings", children: [!model.catalog ? (
                // auto-ads без каталога не проверит настройки шаблона и запуск не примет
                _jsx(CatalogMissingNotice, { model: model, control: control, variant: "danger", unavailableText: "\u0417\u0430\u043F\u0443\u0441\u043A \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435\u0432\u043E\u0437\u043C\u043E\u0436\u0435\u043D: \u0441\u043F\u0438\u0441\u043E\u043A \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u0435\u043D, \u043F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u043F\u043E\u0437\u0436\u0435." })) : (_jsx(FunnelCatalogStaleNotice, { catalog: model.catalog })), model.editable.map((d) => (_jsx("div", { "data-testid": `launch-funnel-${funnelField(d.key)}`, children: _jsx(FunnelSettingField, { def: d, targeted: model.targeted.has(d.key), dims: model.dims }) }, d.key))), model.blocked.length > 0 ? (_jsxs("div", { className: "flex flex-col gap-1", "data-testid": "funnel-blocked", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0428\u0430\u0431\u043B\u043E\u043D \u0440\u0430\u0437\u0440\u0435\u0448\u0430\u0435\u0442 \u043C\u0435\u043D\u044F\u0442\u044C \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435, \u043D\u043E \u0441\u0435\u0439\u0447\u0430\u0441 \u0437\u0434\u0435\u0441\u044C \u043D\u0435 \u043C\u0435\u043D\u044F\u0435\u0442\u0441\u044F \u2014 \u0443\u0439\u0434\u0451\u0442 \u043A\u0430\u043A \u0432 \u0448\u0430\u0431\u043B\u043E\u043D\u0435:" }), _jsx("ul", { className: "flex flex-col gap-1 text-sm", children: model.blocked.map((b) => {
                                const v = model.values[b.key];
                                return (_jsxs("li", { "data-testid": "funnel-blocked-item", children: [_jsxs("span", { className: "text-[var(--color-text-muted)]", children: [funnelLabelOf(b.def), ": "] }), v === undefined
                                            ? FUNNEL_NOT_SET_LABEL.toLowerCase()
                                            : funnelValueText(b.def, v), _jsx("span", { className: "block text-xs text-[var(--color-text-muted)]", children: b.reason === 'unavailable'
                                                ? FUNNEL_UNAVAILABLE_TEXT
                                                : 'Это значение форма пока не умеет менять — оно останется как в шаблоне' })] }, b.key));
                            }) })] })) : null, Object.keys(fixed).length > 0 ? (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0417\u0430\u0434\u0430\u043D\u043E \u0448\u0430\u0431\u043B\u043E\u043D\u043E\u043C, \u043F\u0440\u0438 \u0437\u0430\u043F\u0443\u0441\u043A\u0435 \u043D\u0435 \u043C\u0435\u043D\u044F\u0435\u0442\u0441\u044F:" }), _jsx(FunnelValuesList, { values: fixed, catalog: model.catalog, testId: "funnel-fixed" })] })) : null, model.editable.length === 0 &&
                    model.blocked.length === 0 &&
                    Object.keys(fixed).length === 0 ? (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u043D\u0435 \u0437\u0430\u0434\u0430\u043D\u044B." })) : null] }) }));
}
/** Сводка настроек воронки в плане (предпросмотре) — словами. */
export function FunnelPlanSummary({ values, catalog, }) {
    const empty = Object.keys(values).length === 0;
    return (_jsx(SectionCard, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u0432 \u043F\u043B\u0430\u043D\u0435", children: _jsx("div", { "data-testid": "plan-funnel-settings", children: empty ? (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u041D\u0435 \u0437\u0430\u0434\u0430\u043D\u044B \u2014 \u0432\u043E\u0440\u043E\u043D\u043A\u0430 \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442 \u043A\u0430\u043A \u043E\u0431\u044B\u0447\u043D\u043E." })) : (_jsx(FunnelValuesList, { values: values, catalog: catalog })) }) }));
}
// ---------------------------------------------------------------------------
// Карточка запуска (только чтение, решение Р5)
// ---------------------------------------------------------------------------
/**
 * Блок «Настройки воронки» карточки запуска: ТЕКУЩАЯ версия настроек
 * (`GET /funnel-settings?launchId=`); запрос не удался или ещё идёт —
 * версия, записанная в запуске при создании.
 */
export function LaunchFunnelSettingsCard({ launchId, fallback, }) {
    const catalog = useFunnelSettingsCatalog();
    const current = useQuery({
        ...ADS_QUERY_DEFAULTS,
        queryKey: ['ads', 'funnel-settings', 'launch', launchId],
        queryFn: () => getCampaignFunnelSettings({ launchId }),
        retry: false,
    });
    const cur = current.data?.current ?? null;
    const settings = cur ? { values: cur.values, revision: cur.revision } : fallback;
    const tombstoned = cur?.state === 'tombstoned';
    const hasValues = Object.keys(settings.values).length > 0;
    return (_jsx(SectionCard, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438", hint: settings.revision == null ? undefined : (_jsxs(Badge, { variant: "outline", children: ["\u0432\u0435\u0440\u0441\u0438\u044F ", settings.revision] })), children: _jsxs("div", { className: "flex flex-col gap-2", "data-testid": "launch-funnel-card", children: [tombstoned ? (_jsx("div", { children: _jsx(Badge, { variant: "warning", "data-testid": "funnel-tombstoned", children: "\u0417\u0430\u043F\u0443\u0441\u043A \u043E\u0442\u043A\u0430\u0447\u0435\u043D \u2014 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u044E\u0442" }) })) : null, !hasValues ? (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", children: "\u041D\u0435 \u0437\u0430\u0434\u0430\u043D\u044B \u2014 \u0432\u043E\u0440\u043E\u043D\u043A\u0430 \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442 \u043A\u0430\u043A \u043E\u0431\u044B\u0447\u043D\u043E." })) : catalog.isPending ? (_jsx(FunnelCatalogLoading, { height: "h-12", testId: "funnel-card-loading" })) : (_jsx(FunnelValuesList, { values: settings.values, catalog: catalog.data ?? null })), current.error ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", "data-testid": "funnel-current-failed", children: ["\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u043E\u043B\u0443\u0447\u0438\u0442\u044C \u0442\u0435\u043A\u0443\u0449\u0443\u044E \u0432\u0435\u0440\u0441\u0438\u044E \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A (", describeAdsError(current.error), ") \u2014 \u043F\u043E\u043A\u0430\u0437\u0430\u043D\u044B \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043D\u0430 \u043C\u043E\u043C\u0435\u043D\u0442 \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u044F \u0437\u0430\u043F\u0443\u0441\u043A\u0430."] })) : null, catalog.error ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: funnelCatalogProblemText(catalog.error) })) : null, _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u0432\u043E\u0440\u043E\u043D\u043A\u0438 \u0443 \u0441\u043E\u0437\u0434\u0430\u043D\u043D\u043E\u0439 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0438 \u043F\u043E\u043A\u0430 \u043D\u0435\u043B\u044C\u0437\u044F." })] }) }));
}
//# sourceMappingURL=funnel-settings.js.map